import { defineComponent as _defineComponent } from 'vue'
import { AmountType, CreateTradeRequest, VanillaPriceResponse } from 'ah-api-gateways';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { computed, ref, watch } from 'vue';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import OptionTermsheetModal from './modals/OptionTermsheetModal.vue';
import { useAhTradesState } from '..';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { waitForEntityCreation } from 'ah-requests';
import { of } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { useToast } from 'ah-common-lib/src/toast/Toast';

enum OptionsCreatorSteps {
  OPTIONS_CREATOR = 'optionCreator',
  OPTIONS_REVIEW = 'optionsReview',
  OPTIONS_BOOKED = 'optionsBooked',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OptionReview',
  props: {
    solution: null,
    loading: { type: Boolean },
    error: { type: Boolean },
    pause: { type: Boolean }
  },
  emits: ["update:stage", "remove-selected-solution", "update:createdTrade", "reload-prices", "update:createLoading", "update:pause"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:stage', value: OptionsCreatorSteps): void;
  (e: 'remove-selected-solution'): void;
  (e: 'update:createdTrade', value: string): void;
  (e: 'reload-prices'): void;
  (e: 'update:createLoading', value: boolean): void;
  (e: 'update:pause', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { solution: VanillaPriceResponse; loading: boolean; error: boolean; pause: boolean };

const optionTermsheetModal = ref<InstanceType<typeof OptionTermsheetModal> | null>(null);

const requestManager = useRequestManager();

const toast = useToast();



const tradeState = useAhTradesState();



const agentLoading = computed(() => requestManager.manager.requestStates.createTrade === 'pending');

watch(agentLoading, () => emit('update:createLoading', agentLoading.value), { immediate: true });

function goBack() {
  emit('update:stage', OptionsCreatorSteps.OPTIONS_CREATOR);
  emit('remove-selected-solution');
}

const isPremiumCcy = computed(() => {
  if (props.solution.ccy1.currency === props.solution.premiumCurrency) {
    return props.solution.ccy1;
  }
  return props.solution.ccy2;
});

const isPrimaryRate = computed(() => {
  if (props.solution.ccy1.isPrimaryRate === true) {
    return props.solution.ccy1;
  }
  return props.solution.ccy2;
});

const alt21Costs = computed(() => formatCurrencyValue(isPremiumCcy.value.optionsCurrencyData.lpPremium) ?? '-');

const alt21Markup = computed(() => formatCurrencyValue(isPremiumCcy.value.optionsCurrencyData.markupPremium) ?? '-');

const alt21Credit = computed(() => formatCurrencyValue(isPremiumCcy.value.optionsCurrencyData.creditPremium) ?? '-');

const totalCosts = computed(() => formatCurrencyValue(isPremiumCcy.value.optionsCurrencyData.totalPremium) ?? '-');

const sellAmount = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.SELL) {
    return formatCurrencyValue(props.solution.ccy1.clientAmount);
  }

  return formatCurrencyValue(props.solution.ccy2.clientAmount);
});

const onBehalfOfClient = useOnBehalfOf();

const sellCurrency = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.SELL) {
    return props.solution.ccy1.currency;
  }

  return props.solution.ccy2.currency;
});

const buyAmount = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.BUY) {
    return formatCurrencyValue(props.solution.ccy1.clientAmount);
  }

  return formatCurrencyValue(props.solution.ccy2.clientAmount);
});

const buyCurrency = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.BUY) {
    return props.solution.ccy1.currency;
  }

  return props.solution.ccy2.currency;
});

function viewTermsheet() {
  optionTermsheetModal.value!.show();
}

function createTrade() {
  let createTradeRequest: CreateTradeRequest = {
    priceId: props.solution.id,
    hedgingProduct: props.solution.hedgingProduct,
    syncPendingFunds: false,
    premiumCurrency: props.solution.premiumCurrency,
  };

  requestManager.manager
    .sameOrCancelAndNew(
      'createTrade',
      tradeState.services.trade.createTrade(createTradeRequest, onBehalfOfClient.value?.id).pipe(
        mergeMap((idEntity) =>
          waitForEntityCreation(() =>
            tradeState.services.trade.getTrade(idEntity.id, { errors: { silent: true } })
          ).pipe(
            mergeMap((response) => of(response.referenceNumber)),
            catchError(() => of(null))
          )
        )
      ),
      createTradeRequest
    )
    .subscribe((response) => {
      if (response !== null) {
        emit('update:createdTrade', response);
        toast.success('Trade created successfully');
        emit('update:stage', OptionsCreatorSteps.OPTIONS_BOOKED);
      }
    });
}

return { __sfc: true,OptionsCreatorSteps, optionTermsheetModal, requestManager, toast, props, tradeState, emit, agentLoading, goBack, isPremiumCcy, isPrimaryRate, alt21Costs, alt21Markup, alt21Credit, totalCosts, sellAmount, onBehalfOfClient, sellCurrency, buyAmount, buyCurrency, viewTermsheet, createTrade, formatCurrencyValue, formatDate, OptionTermsheetModal }
}

})